"use client";

import { useEffect, useState } from "react";
import {
  fetchJudgeStatsDemarcations,
  fetchUserJudgeStats,
  fetchUserUsageSummary,
  type JudgeStatsDemarcation,
} from "@/app/lib/api";
import { getContent, subscribeToContent } from "@/app/lib/content";
import MarkdownRenderer from "@/app/components/chat/MarkdownRenderer";
import UsageSections, {
  buildSectionsFromSummary,
} from "./UsageSections";

const JUDGE_DEMARC_LS_KEY = "trio.judgeStats.since";
const JUDGE_DEMARC_DEFAULT_AT_PICK_LS_KEY = "trio.judgeStats.defaultAtPick";

// =============================================================================
// Helpers (duplicated from AdminPanel.tsx — not exported there).
// Keep in sync with AdminPanel.
// =============================================================================

const PROVIDER_ORDER = ["anthropic", "gemini", "openai"] as const;

function modelLabel(provider: string, mode: string): string {
  const key = `${provider}|${mode}`;
  const labels: Record<string, string> = {
    "anthropic|fast": "Claude Haiku 4.5",
    "anthropic|thinking": "Claude Opus 4.7 (think)",
    "anthropic|pro": "Claude Opus 4.7 (pro)",
    "anthropic|deep_research": "Claude Opus 4.7 (deep)",
    "openai|fast": "GPT-5.5 (fast)",
    "openai|thinking": "GPT-5.5 (thinking)",
    "openai|pro": "GPT-5.5 Pro",
    "openai|deep_research": "OpenAI o3",
    "openai|tts": "OpenAI tts-1",
    "gemini|fast": "Gemini Flash 3 (fast)",
    "gemini|thinking": "Gemini Pro 3.1 (think)",
    "gemini|pro": "Gemini Pro 3.1",
    "gemini|deep_research": "Gemini Pro 3.1 (deep)",
  };
  return labels[key] || `${provider} ${mode}`;
}

/** Label for a per-model breakdown row. Prefers the raw model id from the
 * server's `by_model` (most specific); falls back to the friendly
 * (provider, mode) label for pre-overhaul buckets with no model id. */
function modelLabelFor(provider: string, mode: string, model: string): string {
  return model && model.trim() ? model : modelLabel(provider, mode);
}

function providerDotColor(provider: string): string {
  switch (provider) {
    case "anthropic":
      return "#cc6144";
    case "gemini":
      return "#6b5bff";
    case "openai":
      return "#14a37f";
    default:
      return "#888";
  }
}

function providerDisplayName(provider: string): string {
  switch (provider) {
    case "anthropic":
      return "Anthropic";
    case "gemini":
      return "Gemini";
    case "openai":
      return "OpenAI";
    default:
      return provider;
  }
}

function formatCost(value: number | undefined | null): string {
  if (value === undefined || value === null) return "$0.00";
  return `$${Number(value).toFixed(2)}`;
}

function formatTokens(count: number | undefined | null): string {
  const n = Number(count) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return `${n}`;
}

// =============================================================================
// Shared table styles (duplicated from AdminPanel.tsx)
// =============================================================================

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: "1px solid var(--border)",
  color: "var(--text-tertiary)",
  fontWeight: 600,
  fontSize: "11px",
};

const thRightStyle: React.CSSProperties = { ...thStyle, textAlign: "right" };

const tdStyle: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid var(--border)",
  color: "var(--text-primary)",
};

const tdRightStyle: React.CSSProperties = { ...tdStyle, textAlign: "right" };

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "var(--text-tertiary)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "6px",
};

// =============================================================================
// Types
// =============================================================================

type JudgeStats = Awaited<ReturnType<typeof fetchUserJudgeStats>>;
type UsageSummary = Awaited<ReturnType<typeof fetchUserUsageSummary>>;

// =============================================================================
// Judging Section
// =============================================================================

function JudgingSection({
  data,
  loading,
  error,
  demarcations,
  selectedSince,
  onChangeSince,
}: {
  data: JudgeStats | null;
  loading: boolean;
  error: string | null;
  demarcations: JudgeStatsDemarcation[];
  selectedSince: string | null;
  onChangeSince: (key: string) => void;
}) {
  const content = getContent()["usage.judging"];
  const [, setContentEpoch] = useState(0);

  useEffect(() => subscribeToContent(() => setContentEpoch((epoch) => epoch + 1)), []);

  const sourceColumns = {
    provider: (provider: string, _values: Record<string, string>) => (
      <td key="provider" style={tdStyle}>
        <span
          style={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: providerDotColor(provider),
            marginRight: "8px",
            verticalAlign: "middle",
          }}
        />
        {providerDisplayName(provider)}
      </td>
    ),
    win_pct: (_provider: string, values: Record<string, string>) => <td key="win_pct" style={tdRightStyle}>{values.win_pct}</td>,
    avg_score: (_provider: string, values: Record<string, string>) => <td key="avg_score" style={tdRightStyle}>{values.avg_score}</td>,
    bias_pct: (_provider: string, values: Record<string, string>) => <td key="bias_pct" style={tdRightStyle}>{values.bias_pct}</td>,
    avg_self: (_provider: string, values: Record<string, string>) => <td key="avg_self" style={tdRightStyle}>{values.avg_self}</td>,
  } as const;
  const columns = content.columns.filter(
    (column): column is typeof content.columns[number] & { id: keyof typeof sourceColumns } =>
      column.id in sourceColumns,
  );
  const explainerBlocks = content.blocks.filter((block) => block.id !== "window-caveat");
  const caveatBlock = content.blocks.find((block) => block.id === "window-caveat");

  return (
    <div>
      <div style={sectionHeadingStyle}>Judging</div>
      {demarcations.length > 0 && (
        <div style={{ marginBottom: "8px" }}>
          <label
            htmlFor="judge-since"
            style={{
              fontSize: "11px",
              color: "var(--text-tertiary)",
              marginRight: "8px",
            }}
          >
            Window:
          </label>
          <select
            id="judge-since"
            value={selectedSince ?? ""}
            onChange={(e) => onChangeSince(e.target.value)}
            style={{
              // Match the Usage month selector below — same background,
              // border-radius token, and padding shape so the two
              // dropdowns read as a single visual family on the page.
              fontSize: "12px",
              padding: "6px 26px 6px 10px",
              background: "#141414",
              color: "var(--text-primary)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              cursor: "pointer",
              boxShadow: "none",
              outline: "none",
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 8px center",
            }}
          >
            {demarcations.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {loading ? (
        <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>
      ) : !data ? null : (
        <>
          <div
            style={{
              fontSize: "12px",
              color: "var(--text-tertiary)",
              marginBottom: "8px",
            }}
          >
            Total judging runs: <strong>{data.total_runs}</strong>
          </div>
          <table style={tableStyle}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.id} style={column.align === "leading" ? thStyle : thRightStyle}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PROVIDER_ORDER.map((provider) => {
                const stats = data.per_provider[provider];
                const winPct =
                  stats && stats.judged > 0
                    ? `${((stats.wins / stats.judged) * 100).toFixed(1)}%`
                    : "-";
                const biasPct =
                  stats && stats.self_and_judged > 0
                    ? `${((stats.self_wins / stats.self_and_judged) * 100).toFixed(1)}%`
                    : "-";
                const avgScore =
                  stats && stats.avg_score_received != null
                    ? stats.avg_score_received.toFixed(2)
                    : "-";
                const avgSelf =
                  stats && stats.avg_self_score != null
                    ? stats.avg_self_score.toFixed(2)
                    : "-";
                return (
                  <tr key={provider}>
                    {columns.map((column) =>
                      sourceColumns[column.id](provider, {
                        win_pct: winPct,
                        avg_score: avgScore,
                        bias_pct: biasPct,
                        avg_self: avgSelf,
                      }),
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.bias_metrics && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "8px",
                background: "var(--surface-hover)",
                fontSize: "12px",
                lineHeight: 1.5,
              }}
            >
              {data.bias_metrics.rows && data.bias_metrics.rows.length > 0 ? (
                data.bias_metrics.rows.map((row, index, rows) => (
                  <div key={row.id} style={index < rows.length - 1 ? { marginBottom: "6px" } : undefined}>
                    <strong>{row.label}:</strong> {row.value}
                  </div>
                ))
              ) : (
                <>
                  <div style={{ marginBottom: "6px" }}>
                    <strong>OAI premium on OAI:</strong>{" "}
                    {data.bias_metrics.oai_premium_on_oai != null
                      ? `+${data.bias_metrics.oai_premium_on_oai.toFixed(2)}`
                      : "—"}
                  </div>
                  <div style={{ marginBottom: "6px" }}>
                    <strong>OpenAI premium vs others:</strong>{" "}
                    {data.bias_metrics.openai_premium_vs_others != null
                      ? `+${data.bias_metrics.openai_premium_vs_others.toFixed(2)}`
                      : "—"}
                  </div>
                  <div>
                    <strong>Trio OAI self-penalty:</strong> −{data.bias_metrics.trio_oai_self_penalty.toFixed(2)}
                  </div>
                </>
              )}
              {data.bias_metrics.panel_runs != null && (
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "11px",
                    color: "var(--text-tertiary)",
                    fontStyle: "italic",
                  }}
                >
                  <MarkdownRenderer
                    content={(caveatBlock?.text || "").replaceAll("{panel_runs}", String(data.bias_metrics.panel_runs)).replaceAll("{runs_word}", data.bias_metrics.panel_runs === 1 ? "run" : "runs")}
                  />
                </div>
              )}
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "11px",
                  color: "var(--text-tertiary)",
                  lineHeight: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {explainerBlocks.map((block) => (
                  <MarkdownRenderer key={block.id} content={block.text} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Usage Section
// =============================================================================

// Sentinel month key for the synthesized "All time" aggregation.
// Lives in module scope so the iOS-paired user defaults / future logic
// can share it; not actually persisted server-side.
const ALL_TIME_KEY = "__all__";
const ALL_TIME_LABEL = "All time";

type UsageRowShape = UsageSummary["months"][number]["rows"][number];
type UsageMonthShape = UsageSummary["months"][number];
type UsageSectionShape = NonNullable<UsageSummary["months"][number]["sections"]>[number];

/** Sum every month's rows + sections + totals into a single virtual entry.
 * Rows (and their nested `by_model` arrays) are deep-copied so the merged
 * "All time" entry never aliases a per-month array. */
function buildAllTimeEntry(months: UsageMonthShape[]): UsageMonthShape {
  const rowMap = new Map<string, UsageRowShape>();
  const sectMap = new Map<string, UsageSectionShape>();
  let cost = 0, reqs = 0, tin = 0, tout = 0;
  for (const m of months) {
    cost += m.total_cost_usd || 0;
    reqs += m.total_requests || 0;
    tin += m.total_input_tokens || 0;
    tout += m.total_output_tokens || 0;
    for (const r of m.rows || []) {
      const key = `${r.provider}|${r.mode}`;
      const existing = rowMap.get(key);
      if (existing) {
        existing.requests += r.requests;
        existing.web_search_count = (existing.web_search_count ?? 0) + (r.web_search_count ?? 0);
        existing.input_tokens += r.input_tokens;
        existing.output_tokens += r.output_tokens;
        existing.cost_usd += r.cost_usd;
        // Merge nested per-model rows by model id.
        const byModel = existing.by_model || [];
        for (const bm of r.by_model || []) {
          const found = byModel.find((x) => x.model === bm.model);
          if (found) {
            found.requests += bm.requests;
            found.web_search_count = (found.web_search_count ?? 0) + (bm.web_search_count ?? 0);
            found.input_tokens += bm.input_tokens;
            found.output_tokens += bm.output_tokens;
            found.cost_usd += bm.cost_usd;
          } else {
            byModel.push({ ...bm });
          }
        }
        existing.by_model = byModel;
      } else {
        rowMap.set(key, {
          ...r,
          by_model: (r.by_model || []).map((bm) => ({ ...bm })),
        });
      }
    }
    for (const s of m.sections || []) {
      const existing = sectMap.get(s.section);
      if (existing) {
        existing.requests += s.requests;
        existing.web_search_count = (existing.web_search_count ?? 0) + (s.web_search_count ?? 0);
        existing.input_tokens += s.input_tokens;
        existing.output_tokens += s.output_tokens;
        existing.cost_usd += s.cost_usd;
      } else {
        sectMap.set(s.section, { ...s });
      }
    }
  }
  return {
    month: ALL_TIME_KEY,
    total_cost_usd: cost,
    total_requests: reqs,
    total_input_tokens: tin,
    total_output_tokens: tout,
    rows: Array.from(rowMap.values()),
    sections: Array.from(sectMap.values()),
  };
}

/** Group rows by provider, summing requests + cost across all modes.
 * Output keyed by provider id; missing providers are absent (caller
 * iterates PROVIDER_ORDER and shows "$0.00" placeholders). Audio (TTS)
 * requests are excluded from the count (their cost is still summed in)
 * so the provider's chat-request signal isn't buried by voice-mode noise. */
function rollupByProvider(rows: UsageRowShape[]): Record<string, { requests: number; cost_usd: number }> {
  const acc: Record<string, { requests: number; cost_usd: number }> = {};
  for (const r of rows) {
    if (!acc[r.provider]) acc[r.provider] = { requests: 0, cost_usd: 0 };
    if (r.mode !== "audio" && r.mode !== "tts") acc[r.provider].requests += r.requests;
    acc[r.provider].cost_usd += r.cost_usd;
  }
  return acc;
}

function UsageSection({
  data,
  loading,
  error,
  selectedMonth,
  setSelectedMonth,
}: {
  data: UsageSummary | null;
  loading: boolean;
  error: string | null;
  selectedMonth: string;
  setSelectedMonth: (m: string) => void;
}) {
  if (loading) {
    return (
      <div>
        <div style={sectionHeadingStyle}>Usage</div>
        <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <div style={sectionHeadingStyle}>Usage</div>
        <div style={{ color: "#e55", fontSize: "12px" }}>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  // Resolve `selected` to either a real month entry, the synthesized
  // "All time" aggregation, or fall back to the newest month.
  const allTimeEntry = data.months.length > 0 ? buildAllTimeEntry(data.months) : null;
  const selected: UsageMonthShape | undefined =
    selectedMonth === ALL_TIME_KEY
      ? allTimeEntry ?? undefined
      : data.months.find((m) => m.month === selectedMonth) || data.months[0];

  // Per-provider totals strip still rolls up the flat rows.
  const flatRows: UsageRowShape[] = selected?.rows || [];
  const providerTotals = rollupByProvider(flatRows);

  // Collapsible sections — one per mode, fixed order, collapsed by default.
  const usageSections = buildSectionsFromSummary(
    selected?.rows,
    selected?.sections,
    modelLabelFor
  );

  return (
    <div>
      <div style={sectionHeadingStyle}>Usage</div>

      {/* Month selector — "All time" first, then newest-first months */}
      <div style={{ marginBottom: "10px" }}>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: "6px 26px 6px 10px",
            background: "#141414",
            color: "var(--text-primary)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontSize: "12px",
            outline: "none",
            // Flat look — strip browser-default 3D gradient + native
            // arrow, draw our own chevron via background-image. Same
            // pattern as the Window selector above.
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            cursor: "pointer",
            boxShadow: "none",
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 8px center",
          }}
        >
          <option value={ALL_TIME_KEY}>{ALL_TIME_LABEL}</option>
          {data.months.map((m) => (
            <option key={m.month} value={m.month}>
              {m.month}
            </option>
          ))}
        </select>
      </div>

      {/* Total card: Lifetime + Selected scope */}
      <div
        style={{
          padding: "14px 16px",
          background: "var(--bg)",
          borderRadius: "var(--radius-md)",
          display: "flex",
          gap: "12px",
          justifyContent: "space-around",
          marginBottom: "12px",
          textAlign: "center",
        }}
      >
        {/* Lifetime */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "11px",
              color: "var(--text-tertiary)",
              marginBottom: "4px",
            }}
          >
            Lifetime
          </div>
          <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--text-primary)" }}>
            {formatCost(data.lifetime.total_cost_usd)}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
            {data.lifetime.total_requests} requests
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "2px" }}>
            {formatTokens(data.lifetime.total_input_tokens)} in ·{" "}
            {formatTokens(data.lifetime.total_output_tokens)} out
          </div>
        </div>

        {/* Selected scope (month or All time) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "11px",
              color: "var(--text-tertiary)",
              marginBottom: "4px",
            }}
          >
            {selected?.month === ALL_TIME_KEY ? ALL_TIME_LABEL : selected?.month ?? "-"}
          </div>
          <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--text-primary)" }}>
            {formatCost(selected?.total_cost_usd)}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
            {selected?.total_requests ?? 0} requests
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-tertiary)", marginTop: "2px" }}>
            {formatTokens(selected?.total_input_tokens)} in ·{" "}
            {formatTokens(selected?.total_output_tokens)} out
          </div>
        </div>
      </div>

      {/* Per-provider totals strip — three cells, requests + cost per provider */}
      {flatRows.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          {PROVIDER_ORDER.map((provider) => {
            const t = providerTotals[provider] || { requests: 0, cost_usd: 0 };
            return (
              <div
                key={provider}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: "var(--surface-hover)",
                  borderRadius: "var(--radius-md)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-tertiary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: providerDotColor(provider),
                    }}
                  />
                  {providerDisplayName(provider)}
                </div>
                <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--text-primary)" }}>
                  {formatCost(t.cost_usd)}
                </div>
                <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                  {t.requests} reqs
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Collapsible per-section breakdown — one section per mode in the
          fixed order Fast / Thinking / Pro / Deep Research / Judging /
          Audio / Other. Collapsed by default; click a header to reveal
          the per-model rows. */}
      <UsageSections sections={usageSections} />

      <div
        style={{
          fontSize: "11px",
          color: "var(--text-tertiary)",
          marginTop: "8px",
          lineHeight: 1.4,
        }}
      >
        {data.cost_note}
      </div>
    </div>
  );
}

// =============================================================================
// UsagePanel
// =============================================================================

export default function UsagePanel() {
  const [judgeStats, setJudgeStats] = useState<JudgeStats | null>(null);
  const [judgeLoading, setJudgeLoading] = useState(true);
  const [judgeError, setJudgeError] = useState<string | null>(null);

  // Demarcation (time-window) selector for judge-stats. Server-driven list;
  // server computes the default. Persisted to localStorage so the user's
  // last choice sticks across visits.
  const [demarcations, setDemarcations] = useState<JudgeStatsDemarcation[]>([]);
  const [selectedSince, setSelectedSince] = useState<string | null>(null);

  const [usageData, setUsageData] = useState<UsageSummary | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<string>("");

  // Resolve a persisted choice only when it was made against the current
  // server default. A newer default supersedes older choices.
  function resolveInitialSince(list: JudgeStatsDemarcation[]): string | null {
    if (typeof window === "undefined") return null;
    const def = list.find((d) => d.default);
    const defaultKey = def?.key ?? (list[0]?.key ?? null);
    try {
      const stored = window.localStorage.getItem(JUDGE_DEMARC_LS_KEY);
      const defaultAtPick = window.localStorage.getItem(JUDGE_DEMARC_DEFAULT_AT_PICK_LS_KEY);
      if (stored && defaultAtPick === defaultKey && list.some((d) => d.key === stored)) return stored;
      window.localStorage.removeItem(JUDGE_DEMARC_LS_KEY);
      window.localStorage.removeItem(JUDGE_DEMARC_DEFAULT_AT_PICK_LS_KEY);
    } catch {
      /* localStorage disabled — fall through */
    }
    return defaultKey;
  }

  // Fetch demarcations once + initial judge-stats. Subsequent stat refetches
  // are driven by selectedSince via the second useEffect below.
  useEffect(() => {
    let cancelled = false;

    fetchJudgeStatsDemarcations()
      .then((res) => {
        if (cancelled) return;
        const list = res.demarcations || [];
        setDemarcations(list);
        const initial = resolveInitialSince(list);
        if (initial) setSelectedSince(initial);
      })
      .catch(() => {
        /* fall back to default behavior (server picks); selectedSince stays null */
      });

    setUsageLoading(true);
    fetchUserUsageSummary()
      .then((d) => {
        if (!cancelled) {
          setUsageData(d);
          setSelectedMonth(d.current_month || (d.months[0]?.month ?? ""));
        }
      })
      .catch((e) => {
        if (!cancelled) setUsageError(e?.message || "Failed to load usage summary");
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // (Re)fetch judge-stats whenever the selected demarcation changes. Also
  // covers the very first fetch (selectedSince starts null → fetch with no
  // `since` → server picks default; then demarcations land + selectedSince
  // is set → re-fetch with the explicit key, which always matches the
  // server default on first run).
  useEffect(() => {
    let cancelled = false;
    setJudgeLoading(true);
    fetchUserJudgeStats(selectedSince)
      .then((d) => {
        if (!cancelled) {
          setJudgeStats(d);
          setJudgeError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setJudgeError(e?.message || "Failed to load judging stats");
      })
      .finally(() => {
        if (!cancelled) setJudgeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSince]);

  function onChangeSince(key: string) {
    setSelectedSince(key);
    try {
      window.localStorage.setItem(JUDGE_DEMARC_LS_KEY, key);
      const defaultKey = demarcations.find((d) => d.default)?.key ?? demarcations[0]?.key;
      if (defaultKey) window.localStorage.setItem(JUDGE_DEMARC_DEFAULT_AT_PICK_LS_KEY, defaultKey);
    } catch {
      /* localStorage disabled */
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "12px 0",
      }}
    >
      <JudgingSection
        data={judgeStats}
        loading={judgeLoading}
        error={judgeError}
        demarcations={demarcations}
        selectedSince={selectedSince}
        onChangeSince={onChangeSince}
      />
      <UsageSection
        data={usageData}
        loading={usageLoading}
        error={usageError}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />
    </div>
  );
}
